import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import './Alerts.scss';

// ─── EmailJS config ───────────────────────────────────────────────────────────
const EMAILJS_SERVICE_ID  = 'service_qbg9q1w';   // e.g. 'service_abc123'
const EMAILJS_PUBLIC_KEY  = 'TDL8B44cg1kFhX2WU';   // e.g. 'user_XYZabc...'

const TEMPLATE_PRICE_ALERT = 'template_n98pj3m';   // see template format below
const TEMPLATE_DIGEST      = 'template_nkhsaxk';
const TEMPLATE_PREFERENCE  = 'template_n98pj3m';
// ─────────────────────────────────────────────────────────────────────────────

const FUEL_OPTIONS = ['E10', 'E5', 'B7', 'SDV', 'EV'];

// Shared send helper
async function sendEmail(templateId, templateParams) {
  return emailjs.send(
    EMAILJS_SERVICE_ID,
    templateId,
    templateParams,
    EMAILJS_PUBLIC_KEY
  );
}

export default function Alerts() {
  // Price Drop Alert
  const [alertPrice,    setAlertPrice]    = useState('');
  const [alertPostcode, setAlertPostcode] = useState('');
  const [alertEmail,    setAlertEmail]    = useState('');
  const [alertStatus,   setAlertStatus]   = useState(null); // null | 'sending' | 'sent' | 'error'

  // Fuel Preference
  const [prefFuel,      setPrefFuel]      = useState('E10');
  const [prefEmail,     setPrefEmail]     = useState('');
  const [prefStatus,    setPrefStatus]    = useState(null);

  // Weekly Digest
  const [digestPc,      setDigestPc]      = useState('');
  const [digestEmail,   setDigestEmail]   = useState('');
  const [digestStatus,  setDigestStatus]  = useState(null);

  // ── handlers ──────────────────────────────────────────────────────────────

  const handlePriceAlert = async () => {
    if (!alertEmail || !alertPrice || !alertPostcode) return;
    setAlertStatus('sending');
    try {
      await sendEmail(TEMPLATE_PRICE_ALERT, {
        to_email:     alertEmail,
        target_price: alertPrice,
        postcode:     alertPostcode.toUpperCase(),
        submitted_at: new Date().toLocaleString('en-GB'),
      });
      setAlertStatus('sent');
      setAlertPrice(''); setAlertPostcode(''); setAlertEmail('');
    } catch {
      setAlertStatus('error');
    }
    setTimeout(() => setAlertStatus(null), 4000);
  };

  const handlePreference = async () => {
    setAlertStatus(null);
    setPrefStatus('sending');
    try {
      await sendEmail(TEMPLATE_PREFERENCE, {
        fuel_type:    prefFuel,
        to_email:     prefEmail || 'no-reply@fuelfinder.co.uk',
        submitted_at: new Date().toLocaleString('en-GB'),
      });
      setPrefStatus('sent');
    } catch {
      setPrefStatus('error');
    }
    setTimeout(() => setPrefStatus(null), 4000);
  };

  const handleDigest = async () => {
    if (!digestEmail || !digestPc) return;
    setDigestStatus('sending');
    try {
      await sendEmail(TEMPLATE_DIGEST, {
        to_email:     digestEmail,
        postcode:     digestPc.toUpperCase(),
        submitted_at: new Date().toLocaleString('en-GB'),
      });
      setDigestStatus('sent');
      setDigestPc(''); setDigestEmail('');
    } catch {
      setDigestStatus('error');
    }
    setTimeout(() => setDigestStatus(null), 4000);
  };

  // ── tiny feedback component ────────────────────────────────────────────────

  const Feedback = ({ status }) => {
    if (!status) return null;
    const map = {
      sending: { cls: 'feedback--sending', msg: 'Sending…' },
      sent:    { cls: 'feedback--sent',    msg: '✓ Message sent!' },
      error:   { cls: 'feedback--error',   msg: '✗ Something went wrong. Try again.' },
    };
    const { cls, msg } = map[status];
    return <p className={`alert-card__feedback ${cls}`}>{msg}</p>;
  };

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <section id="alerts" className="alerts">
      <div className="alerts__inner">
        <div className="alerts__header">
          <p className="alerts__label">Personalised · Free</p>
          <h2 className="alerts__title">Price Alerts &amp; Preferences</h2>
        </div>

        <div className="alerts__grid">

          {/* ── Price Drop Alert ── */}
          <div className="alert-card">
            <div className="alert-card__icon">🔔</div>
            <h3 className="alert-card__title">Price Drop Alert</h3>
            <p className="alert-card__desc">
              We'll notify you when fuel drops below your target price per litre in your area.
            </p>
            <input
              className="alert-card__input"
              type="number"
              placeholder="Target price in pence (e.g. 145)"
              value={alertPrice}
              onChange={(e) => setAlertPrice(e.target.value)}
            />
            <input
              className="alert-card__input"
              type="text"
              placeholder="Your postcode"
              value={alertPostcode}
              onChange={(e) => setAlertPostcode(e.target.value)}
            />
            <input
              className="alert-card__input"
              type="email"
              placeholder="your@email.com"
              value={alertEmail}
              onChange={(e) => setAlertEmail(e.target.value)}
            />
            <Feedback status={alertStatus} />
            <button
              className="alert-card__btn"
              onClick={handlePriceAlert}
              disabled={alertStatus === 'sending'}
            >
              {alertStatus === 'sending' ? 'Sending…' : 'Set Alert →'}
            </button>
          </div>

          {/* ── Saved Stations (no email submission needed) ──
          <div className="alert-card">
            <div className="alert-card__icon">⭐</div>
            <h3 className="alert-card__title">Saved Stations</h3>
            <p className="alert-card__desc">
              Save your favourite stations and get notified when their prices change or they're running low on supply.
            </p>
            <div className="alert-card__empty">
              <span>No saved stations yet.</span>
              <span>Search above and tap ★ to save a station.</span>
            </div>
            <button
              className="alert-card__btn"
              onClick={() => document.getElementById('finder')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Find Stations →
            </button>
          </div> */}

          {/* ── Fuel Preference ── */}
          <div className="alert-card">
            <div className="alert-card__icon">🚗</div>
            <h3 className="alert-card__title">My Fuel Preference</h3>
            <p className="alert-card__desc">
              Set your default fuel type so searches are pre-filtered for your vehicle every time.
            </p>
            <div className="alert-card__chips">
              {FUEL_OPTIONS.map((f) => (
                <button
                  key={f}
                  className={`alert-card__chip${prefFuel === f ? ' alert-card__chip--active' : ''}`}
                  onClick={() => setPrefFuel(f)}
                >
                  {f}
                </button>
              ))}
            </div>
            {/* Optional: collect email so user can receive confirmation */}
            <input
              className="alert-card__input"
              type="email"
              placeholder="your@email.com (optional)"
              value={prefEmail}
              onChange={(e) => setPrefEmail(e.target.value)}
            />
            <Feedback status={prefStatus} />
            <button
              className="alert-card__btn"
              onClick={handlePreference}
              disabled={prefStatus === 'sending'}
            >
              {prefStatus === 'sending' ? 'Saving…' : 'Save Preference →'}
            </button>
          </div>

          {/* ── Weekly Digest ── */}
          <div className="alert-card">
            <div className="alert-card__icon">📊</div>
            <h3 className="alert-card__title">Weekly Digest</h3>
            <p className="alert-card__desc">
              A weekly email with regional price trends, best-value stations near you, and the best time to fill up.
            </p>
            <input
              className="alert-card__input"
              type="text"
              placeholder="Your postcode"
              value={digestPc}
              onChange={(e) => setDigestPc(e.target.value)}
            />
            <input
              className="alert-card__input"
              type="email"
              placeholder="your@email.com"
              value={digestEmail}
              onChange={(e) => setDigestEmail(e.target.value)}
            />
            <Feedback status={digestStatus} />
            <button
              className="alert-card__btn"
              onClick={handleDigest}
              disabled={digestStatus === 'sending'}
            >
              {digestStatus === 'sending' ? 'Subscribing…' : 'Subscribe →'}
            </button>
          </div>

        </div>
      </div>
    </section>
  );
}